import { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'
import './ProductsPage.css'

function ProductsPage() {
  const location = useLocation()

  const scrollToCategory = (categoryId: string) => {
    const element = document.getElementById(categoryId)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }

  // Handle hash navigation when page loads or hash changes
  useEffect(() => {
    if (location.hash) {
      const hash = location.hash.replace('#', '')
      // Small delay to ensure page is rendered
      setTimeout(() => {
        scrollToCategory(hash)
      }, 100)
    }
  }, [location.hash])

  return (
    <div className="products-page">
      <section className="page-hero">
        <div className="container">
          <h1>Available Products</h1>
          <p className="page-hero-subtitle">
            Comprehensive, non-brand specific laboratory equipment and parts. We make it easy for you 
            to find exactly what you need for your lab.
          </p>
        </div>
      </section>

      <section className="products-content">
        <div className="container">
          <div className="products-intro">
            <p>
              With extensive parts knowledge and service expertise, we offer a comprehensive selection of 
              non-brand specific laboratory equipment. Our goal is to make it as easy as possible for you 
              to have exactly the lab you want.
            </p>
            <p>
              Browse our product categories below or contact us at{' '}
              <a href="tel:+15416589374">(541) 658-9374</a>
              {' '}if you have any questions or need assistance finding specific parts or equipment.
            </p>
          </div>

          {/* Category Navigation */}
          <div className="products-category-nav">
            <button onClick={() => scrollToCategory('metal-casework')} className="category-nav-link">
              Metal Casework
            </button>
            <button onClick={() => scrollToCategory('p-lam-casework')} className="category-nav-link">
              P-lam Casework
            </button>
            <button onClick={() => scrollToCategory('phenolic-casework')} className="category-nav-link">
              Phenolic Casework
            </button>
            <button onClick={() => scrollToCategory('countertops')} className="category-nav-link">
              Countertops
            </button>
            <button onClick={() => scrollToCategory('sinks')} className="category-nav-link">
              Sinks
            </button>
            <button onClick={() => scrollToCategory('plumbing')} className="category-nav-link">
              Lab Plumbing Parts
            </button>
            <button onClick={() => scrollToCategory('specialty-cabinets')} className="category-nav-link">
              Specialty Cabinets
            </button>
            <button onClick={() => scrollToCategory('fume-hoods')} className="category-nav-link">
              Fume Hoods
            </button>
            <button onClick={() => scrollToCategory('safety-equipment')} className="category-nav-link">
              Safety Equipment
            </button>
            <button onClick={() => scrollToCategory('safety-audit')} className="category-nav-link">
              Safety Audit
            </button>
            <button onClick={() => scrollToCategory('efficiency-audit')} className="category-nav-link">
              Efficiency Audit
            </button>
          </div>

          {/* Product Categories */}
          <div id="metal-casework" className="product-category-section">
            <h2>Metal Casework</h2>
            <p>
              Durable metal casework options designed for laboratory environments. Available in various 
              configurations and finishes to meet your specific needs.
            </p>
            <div className="product-category-cta">
              <Link to="/casework#quote-form" className="product-cta-link">
                Get a Custom Quote for Metal Casework →
              </Link>
            </div>

            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/projects/technical-manufacturing-lab-1/IMG_0673.jpg" 
                    alt="Metal Casework"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Steel Casework</h3>
                <p>Powder-coated Steel casework units available in various sizes and configurations.</p>
              </div>
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/projects/withycombe-hall/Withycombe- best mobile workstations epoxy counters and mobile carts.jpeg" 
                    alt="Custom Metal Casework"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Custom Metal Casework</h3>
                <p>Custom-designed metal casework to fit your specific laboratory requirements.</p>
              </div>
            </div>
          </div>

          <div id="p-lam-casework" className="product-category-section">
            <h2>P-lam Casework</h2>
            <p>
              Plastic laminate (P-lam) casework offers a cost-effective, attractive solution for laboratory
              environments with moderate chemical exposure. Available in a wide range of colors and finishes
              to match your facility's aesthetic.
            </p>
            <div className="product-category-cta">
              <Link to="/casework#quote-form" className="product-cta-link">
                Get a Custom Quote for P-lam Casework →
              </Link>
            </div>

            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img
                    src="/assets/projects/research-facility-remodel-2/Research Facility - Casework and countertops.jpg"
                    alt="P-lam Casework"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Standard P-lam Casework</h3>
                <p>Plastic laminate casework units in a variety of sizes, configurations, and finish options.</p>
              </div>
            </div>
          </div>

          <div id="phenolic-casework" className="product-category-section">
            <h2>Phenolic Casework</h2>
            <p>
              Phenolic resin casework provides superior chemical resistance and durability for demanding
              laboratory environments. Ideal for facilities working with harsh chemicals, high humidity,
              or requiring enhanced impact resistance.
            </p>
            <div className="product-category-cta">
              <Link to="/casework#quote-form" className="product-cta-link">
                Get a Custom Quote for Phenolic Casework →
              </Link>
            </div>

            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img
                    src="/assets/projects/withycombe-hall/Withycombe Hall P-lam Casework.jpeg"
                    alt="Phenolic Casework"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Phenolic Resin Casework</h3>
                <p>High-performance phenolic casework units built to withstand aggressive chemicals and heavy use.</p>
              </div>
            </div>
          </div>

          <div id="countertops" className="product-category-section">
            <h2>Countertops</h2>
            <p>
              Laboratory countertops in various materials including epoxy, Chemsurf laminate, Corian, 
              and more. Choose the right surface for your application.
            </p>
            <div className="product-category-cta">
              <Link to="/countertops#quote-form" className="product-cta-link">
                Get a Custom Quote for Epoxy, Chemsurf Laminate, Corian, and More →
              </Link>
            </div>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <Link to="/epoxy-countertops">
                    <img
                      src="/assets/projects/withycombe-hall/Withycombe Hall Epoxy Counter and Custom Metal Shelving with split valves.jpeg"
                      alt="Epoxy Countertops"
                      className="product-image"
                      loading="lazy"
                    />
                  </Link>
                </div>
                <h3><Link to="/epoxy-countertops">Epoxy Countertops</Link></h3>
                <p>Chemical-resistant epoxy countertops for demanding laboratory applications.</p>
              </div>
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/projects/withycombe-hall/Withycombe- best mobile workstations epoxy counters and mobile carts.jpeg" 
                    alt="Chemsurf Laminate Countertops"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Chemsurf Laminate Countertops</h3>
                <p>Durable Chemsurf laminate countertops offering excellent chemical resistance and easy maintenance.</p>
              </div>
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/projects/withycombe-hall/Withycombe-plam casework epoxy counters custom island shelves.jpeg" 
                    alt="Corian Countertops"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Corian Countertops</h3>
                <p>Non-porous Corian solid surface countertops ideal for laboratory environments.</p>
              </div>
            </div>
          </div>

          <div id="sinks" className="product-category-section">
            <h2>Sinks</h2>
            <p>
              Laboratory sinks in various materials and configurations to meet your specific needs.
            </p>
            <div className="product-category-cta">
              <Link to="/sinks#quote-form" className="product-cta-link">
                Get a Custom Quote for Laboratory Sinks →
              </Link>
            </div>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/projects/epoxy-repair-1/IMG_0692.PNG" 
                    alt="Laboratory Sinks"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Laboratory Sinks</h3>
                <p>Chemical-resistant, epoxy resin, and stainless steel laboratory sinks, drop-in, under-mount, and ADA.</p>
              </div>
            </div>
          </div>

          <div id="plumbing" className="product-category-section">
            <h2>Miscellaneous Lab Plumbing Parts</h2>
            <p>
              Comprehensive selection of laboratory plumbing components including faucets, fixtures, 
              and specialized plumbing parts.
            </p>
            <div className="product-category-cta">
              <Link to="/sinks#quote-form" className="product-cta-link">
                Get a Custom Quote for Laboratory Plumbing Parts →
              </Link>
            </div>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/faucet.webp" 
                    alt="Lab Plumbing Parts"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Lab Plumbing Components</h3>
                <p>Faucets, fixtures, and specialized plumbing parts for laboratory applications.</p>
              </div>
            </div>
          </div>

          <div id="specialty-cabinets" className="product-category-section">
            <h2>Specialty Cabinets</h2>
            <p>
              Custom and standard specialty cabinets designed for specific laboratory storage needs.
            </p>
            <div className="product-category-cta">
              <Link to="/specialty-cabinets#quote-form" className="product-cta-link">
                Get a Custom Quote for Specialty Cabinets →
              </Link>
            </div>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/projects/withycombe-hall/Withycombe Hall P-lam Casework.jpeg" 
                    alt="Specialty Cabinets"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Specialty Storage Cabinets</h3>
                <p>Custom-designed cabinets for specific storage and organizational needs.</p>
              </div>
            </div>
          </div>

          <div id="fume-hoods" className="product-category-section">
            <h2>Fume Hoods</h2>
            <p>
              Custom and standard fume hoods designed to fit your laboratory equipment and meet safety requirements.
            </p>
            <div className="product-category-cta">
              <Link to="/fume-hoods#quote-form" className="product-cta-link">
                Get a Custom Quote for Fume Hoods and Exhaust Systems →
              </Link>
            </div>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/projects/research-facility-remodel-1/IMG_0174.jpg" 
                    alt="Custom Fume Hoods"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Custom Fume Hoods</h3>
                <p>
                Custom Fume Hoods, designed to maximize safety and efficiency.
                Available in a variety of sizes and styles with multiple customizable safety and efficiency features.
                </p>
                <ul className="product-features">
                  <li>Custom designed to fit your laboratory needs</li>
                  <li>Variety of configuration options</li>
                  <li>Proper fit around specialized equipment</li>
                  <li><strong>Compliance with safety standards</strong> ASHRAE 110 tested and UL Listed</li>
                </ul>
              </div>
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/down-draft-table.jpg" 
                    alt="Down Draft Tables"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Down Draft Tables</h3>
                <p>
                  Patented Down Draft Tables designed to contain gases and particulates. Utilizes 
                  air density differences for effective containment and exhaust.
                </p>
                <ul className="product-features">
                  <li>Patented design for effective containment</li>
                  <li>Contains gases and particulates</li>
                  <li>Utilizes air density differences</li>
                  <li>Safe and efficient exhaust system</li>
                </ul>
              </div>
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/slot-exhauster.jpg" 
                    alt="Slot Exhausters"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Slot Exhausters</h3>
                <p>
                  Ideal for containing odoriferous or low hazardous materials. Available in various 
                  widths and material options.
                </p>
                <ul className="product-features">
                  <li>Ideal for odoriferous or low hazardous materials</li>
                  <li>Available widths: 6 inches to 12 feet</li>
                  <li>Multiple material options</li>
                  <li>Effective containment and exhaust</li>
                </ul>
              </div>
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/snorkel-exhauster.jpg" 
                    alt="Snorkels / Spot Exhausters"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Snorkels / Spot Exhausters</h3>
                <p>
                  Flexible spot exhausters providing adjustability and containment for laboratory bench applications.
                </p>
                <ul className="product-features">
                  <li>Low pressure loss spot exhaust</li>
                  <li>Flexible and adjustable design</li>
                  <li>Ideal for laboratory bench applications</li>
                  <li>Effective containment at the source</li>
                </ul>
              </div>
            </div>
          </div>

          <div id="safety-equipment" className="product-category-section">
            <h2>Safety Equipment</h2>
            <p>
              Comprehensive selection of laboratory safety equipment to ensure compliance and protect your team.
            </p>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/eye-wash-station.webp" 
                    alt="Safety Equipment"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Laboratory Safety Equipment</h3>
                <p>Safety showers, eyewash stations, and other essential safety equipment.</p>
              </div>
            </div>
          </div>

          <div id="safety-audit" className="product-category-section">
            <h2>Safety Audit</h2>
            <p>
              Professional safety audit services to ensure your laboratory meets all safety standards
              and regulatory requirements.
            </p>
            <div className="product-category-cta">
              <Link to="/safety-audit#quote-form" className="product-cta-link">
                Learn About Our Safety Audit Services →
              </Link>
            </div>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <Link to="/safety-audit">
                    <img
                      src="/assets/lab-safety-audit.jpg"
                      alt="Safety Audit Services"
                      className="product-image"
                      loading="lazy"
                    />
                  </Link>
                </div>
                <h3><Link to="/safety-audit">Laboratory Safety Audits</Link></h3>
                <p>Comprehensive safety audits to identify potential hazards and ensure compliance.</p>
              </div>
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img
                    src="/assets/projects/research-facility-remodel-1/IMG_0174.jpg"
                    alt="Fume Hood Testing Services"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Fume Hood Testing</h3>
                <p>
                  Professional fume hood performance testing to verify proper containment and protect
                  your team. We perform ASHRAE 110 testing to evaluate face velocity, airflow
                  visualization, and tracer gas containment.
                </p>
                <ul className="product-features">
                  <li><strong>ASHRAE 110</strong> containment performance testing</li>
                  <li>Face velocity measurement and verification</li>
                  <li>Airflow visualization (smoke testing)</li>
                  <li>Tracer gas containment evaluation</li>
                </ul>
              </div>
            </div>
          </div>

          <div id="efficiency-audit" className="product-category-section">
            <h2>Efficiency Audit</h2>
            <p>
              Professional efficiency audit services to assess and optimize your laboratory operations, 
              workflow, and resource utilization. Our efficiency audits have saved over a million dollars 
              in operating expenses on a single project.
            </p>
            <div className="products-grid">
              <div className="product-card">
                <div className="product-image-wrapper">
                  <img 
                    src="/assets/istockphoto-1389079639-612x612.jpg" 
                    alt="Efficiency Audit Services"
                    className="product-image"
                    loading="lazy"
                  />
                </div>
                <h3>Laboratory Efficiency Audits</h3>
                <p>Comprehensive efficiency assessments to identify optimization opportunities and improve lab productivity. Our audits have saved over a million dollars in operating expenses on a single project.</p>
              </div>
            </div>
          </div>

        </div>
      </section>

      <section className="products-cta-section">
        <div className="container">
          <div className="products-cta-content">
            <h2>Need Help Finding the Right Products?</h2>
            <p>
              Our team has extensive parts knowledge and service expertise. Contact us to discuss your 
              specific needs and we'll help you find exactly what you need for your lab.
            </p>
            <div className="cta-buttons">
              <Link to="/contact" className="hero-cta hero-cta-primary">
                Contact Us
              </Link>
              <a href="tel:+15416589374" className="secondary-cta">
                Call (541) 658-9374
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ProductsPage
